import React, { useState, useRef, useEffect } from "react";
import type { ParsedCountry } from "../../types/PhoneInput";

interface CountryDropdownProps {
  countries: ParsedCountry[];
  selectedCountry: ParsedCountry | null;
  onSelect: (country: ParsedCountry) => void;
  disabled?: boolean;
  searchable?: boolean;
  id?: string;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  countries,
  selectedCountry,
  onSelect,
  disabled = false,
  searchable = true,
  id = "country-dropdown",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter countries based on search query
  const filteredCountries = searchQuery
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.iso2.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.dialCode.includes(searchQuery)
      )
    : countries;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll("li");
      items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCountries.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredCountries[highlightedIndex]) {
          handleSelectCountry(filteredCountries[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectCountry = (country: ParsedCountry) => {
    onSelect(country);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(0);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setHighlightedIndex(0);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setHighlightedIndex(0);
    }
  };

  const countryFlag = selectedCountry
    ? countryToFlag(selectedCountry.iso2)
    : "🌍";

  return (
    <div ref={dropdownRef} className="country-dropdown">
      {/* Dropdown Trigger Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`country-dropdown__trigger ${isOpen ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        id={id}
      >
        <span className="country-dropdown__flag">{countryFlag}</span>
        <span className="country-dropdown__selected">
          {selectedCountry ? (
            <>
              <span className="country-dropdown__dial">
                +{selectedCountry.dialCode}
              </span>
              <span className="country-dropdown__icon"></span>
            </>
          ) : (
            "Select Country"
          )}
        </span>
      </button>

      {isOpen && (
        <div className="country-dropdown__menu">
          {searchable && (
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search country..."
              className="country-dropdown__search"
              value={searchQuery}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              aria-label="Search countries"
            />
          )}

          <ul ref={listRef} className="country-dropdown__list" role="listbox">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <li
                  key={country.iso2}
                  className={`country-dropdown__item ${
                    highlightedIndex === index ? "highlighted" : ""
                  } ${
                    selectedCountry?.iso2 === country.iso2 ? "selected" : ""
                  }`}
                  onClick={() => handleSelectCountry(country)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={selectedCountry?.iso2 === country.iso2}
                >
                  <span className="country-dropdown__item-flag">
                    {countryToFlag(country.iso2)}
                  </span>
                  <span className="country-dropdown__item-info">
                    <span className="country-dropdown__item-name">
                      {country.name}
                    </span>
                    <span className="country-dropdown__item-code">
                      +{country.dialCode}
                    </span>
                  </span>
                  {selectedCountry?.iso2 === country.iso2 && (
                    <span className="country-dropdown__checkmark">✓</span>
                  )}
                </li>
              ))
            ) : (
              <li className="country-dropdown__empty">No countries found</li>
            )}
          </ul>

          <div className="country-dropdown__footer">
            {filteredCountries.length} of {countries.length} countries
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Convert country ISO2 code to flag emoji
 */
function countryToFlag(iso2: string): string {
  const codePoints = iso2
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export default CountryDropdown;
